/**
 * ConfirmDialog — convenience wrapper around the primitive Modal for
 * yes/no confirmations. Inherits Modal's focus trap, body-scroll lock,
 * Esc/backdrop close, ARIA wiring, and portal escape from transformed
 * ancestors.
 *
 * Type → tone mapping:
 *   warning → amber chip + amber CTA (the default)
 *   danger  → rose chip + danger CTA (destructive actions)
 *   success → emerald chip + success CTA
 *   info    → sky chip + brand CTA
 *
 * Backwards-compatible API: same props as the old version. Callers
 * outside the primitive system can keep using it without changes.
 */

import { useTranslation } from 'react-i18next'
import {
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
  HiOutlineInformationCircle,
} from 'react-icons/hi2'
import Modal from './Modal'
import Button from './Button'

const TYPE_CONFIG = {
  warning: {
    Icon: HiOutlineExclamationTriangle,
    chip: 'bg-warning-soft text-warning',
    variant: 'primary',
    ctaTone: '!bg-warning hover:!bg-warning/90 focus-visible:!ring-warning/40',
  },
  danger: {
    Icon: HiOutlineExclamationTriangle,
    chip: 'bg-danger-soft text-danger',
    variant: 'danger',
  },
  success: {
    Icon: HiOutlineCheckCircle,
    chip: 'bg-success-soft text-success',
    variant: 'primary',
    ctaTone: '!bg-success hover:!bg-success/90 focus-visible:!ring-success/40',
  },
  info: {
    Icon: HiOutlineInformationCircle,
    chip: 'bg-info-soft text-info',
    variant: 'primary',
  },
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  type = 'warning',
}) {
  const { t } = useTranslation()
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.warning
  const { Icon, chip, variant, ctaTone } = cfg

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" title={title}>
      <Modal.Body>
        <div className="flex items-start gap-4">
          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${chip}`}>
            <Icon className="w-5 h-5" />
          </span>
          <p className="text-sm text-ink-secondary leading-relaxed pt-1">{message}</p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose}>{cancelText ?? t('common.cancel')}</Button>
        <Button variant={variant} onClick={onConfirm} className={ctaTone}>
          {confirmText ?? t('common.submit')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
